// EVM chain metadata for WalletConnect (CAIP-2 / eip155).
//
// The enterprise app addresses chains by an internal key (`vault.chain`, e.g.
// 'eth'). WalletConnect speaks CAIP — `eip155:<chainId>`. This module is the
// single bridge between the two, plus the human labels the connect UI needs.
//
// Keep the key set in sync with EVM_CHAINS in src/types/vault.ts.

export interface EvmChainMeta {
  /** Internal chain key used across the app and by the backend (vault.chain). */
  key: string;
  /** EIP-155 numeric chain id. */
  chainId: number;
  /** Human-readable network name. */
  name: string;
  /** Native currency symbol. */
  symbol: string;
}

export const EVM_CHAIN_META: readonly EvmChainMeta[] = [
  { key: 'eth', chainId: 1, name: 'Ethereum', symbol: 'ETH' },
  { key: 'bsc', chainId: 56, name: 'BNB Smart Chain', symbol: 'BNB' },
  { key: 'avax', chainId: 43114, name: 'Avalanche C-Chain', symbol: 'AVAX' },
  { key: 'polygon', chainId: 137, name: 'Polygon', symbol: 'POL' },
  { key: 'base', chainId: 8453, name: 'Base', symbol: 'ETH' },
  { key: 'sepolia', chainId: 11155111, name: 'Sepolia', symbol: 'ETH' },
  { key: 'amoy', chainId: 80002, name: 'Polygon Amoy', symbol: 'POL' },
] as const;

const KEY_TO_META = new Map(EVM_CHAIN_META.map((c) => [c.key, c]));
const CHAINID_TO_META = new Map(EVM_CHAIN_META.map((c) => [c.chainId, c]));

/** Metadata for an internal chain key, or undefined for non-EVM / unknown. */
export function chainMetaByKey(key: string): EvmChainMeta | undefined {
  return KEY_TO_META.get(key);
}

/** Metadata for an EIP-155 chain id, or undefined if unsupported. */
export function chainMetaByChainId(chainId: number): EvmChainMeta | undefined {
  return CHAINID_TO_META.get(chainId);
}

/** Internal chain key → numeric EIP-155 chain id (undefined if not EVM). */
export function chainKeyToChainId(key: string): number | undefined {
  return KEY_TO_META.get(key)?.chainId;
}

/** CAIP-2 chain identifier for a vault chain, e.g. 'eth' → 'eip155:1'. */
export function chainKeyToCaip2(key: string): string | undefined {
  const id = chainKeyToChainId(key);
  return id === undefined ? undefined : `eip155:${String(id)}`;
}

/**
 * CAIP-10 account for a vault chain + address, e.g.
 * ('eth', '0xabc…') → 'eip155:1:0xabc…'. Undefined for non-EVM chains.
 */
export function toCaip10Account(key: string, address: string): string | undefined {
  const caip2 = chainKeyToCaip2(key);
  return caip2 === undefined ? undefined : `${caip2}:${address}`;
}

/** Parse a numeric chain id from a WalletConnect chain string ('eip155:137' → 137). */
export function parseEip155ChainId(chain: string): number | undefined {
  const m = /^eip155:(\d+)$/.exec(chain);
  return m ? Number(m[1]) : undefined;
}
